// ⌘K command palette (#1202), after the Tailwind Plus command palette with groups. It searches
// what the CRM already holds in memory (sections, the recipe catalog summary, the master
// ingredients) and offers a user search for whatever was typed. The list is rebuilt each time the
// palette opens, so it always shows the latest snapshot. app.js wires the actions.
import { icon } from "./icons.js";
import { NAV_SECTIONS } from "./shell.js";

const ITEM_CLASSES =
  "group flex cursor-default items-center gap-x-3 px-4 py-2 select-none focus:outline-hidden " +
  "aria-selected:bg-paprika-600 aria-selected:text-white";
const ITEM_ICON = "size-5 flex-none text-gray-400 group-aria-selected:text-white dark:text-gray-500";
const ITEM_META = "ml-auto truncate pl-3 font-mono text-xs text-gray-400 group-aria-selected:text-white/80 dark:text-gray-500";
const GROUP_LABEL = "bg-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-900 dark:bg-white/5 dark:text-white";

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

/** The palette's items, as HTML, from plain data. Exported for the unit test. */
export function paletteItemsHtml({ recipes = {}, ingredients = {} } = {}) {
  const sections = NAV_SECTIONS.map(({ section, label, icon: iconName }) =>
    `<button type="button" data-palette-section="${section}" class="${ITEM_CLASSES} w-full text-left">${icon(iconName, ITEM_ICON)}<span class="truncate">${escapeHtml(label)}</span></button>`
  ).join("");

  const recipeItems = Object.entries(recipes)
    .map(([id, r]) => ({ id, title: r?.title || id, slug: r?.slug || "" }))
    .sort((a, b) => a.title.localeCompare(b.title))
    .map(({ id, title, slug }) =>
      `<button type="button" data-palette-recipe="${escapeHtml(id)}" class="${ITEM_CLASSES} w-full text-left">${icon("book-open", ITEM_ICON)}<span class="truncate">${escapeHtml(title)}</span><span class="${ITEM_META}">${escapeHtml(slug || id)}</span></button>`
    ).join("");

  const ingredientItems = Object.entries(ingredients)
    .map(([key, i]) => ({ key, name: i?.name || i?.display_name || key }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ key, name }) =>
      `<button type="button" data-palette-ingredient="${escapeHtml(key)}" class="${ITEM_CLASSES} w-full text-left">${icon("shopping-bag", ITEM_ICON)}<span class="truncate">${escapeHtml(name)}</span><span class="${ITEM_META}">${escapeHtml(key)}</span></button>`
    ).join("");

  const group = (id, label, items) => items
    ? `<el-command-group hidden aria-labelledby="palette-${id}-label" class="block"><div id="palette-${id}-label" class="${GROUP_LABEL}">${label}</div><div class="mt-1 text-sm text-gray-800 dark:text-gray-300">${items}</div></el-command-group>`
    : "";

  // "Find user" matches any query (see initCommandPalette's filter) and names it.
  const findUser = `<el-command-group hidden aria-labelledby="palette-users-label" class="block"><div id="palette-users-label" class="${GROUP_LABEL}">Users</div><div class="mt-1 text-sm text-gray-800 dark:text-gray-300"><button type="button" data-palette-find-user class="${ITEM_CLASSES} w-full text-left">${icon("users", ITEM_ICON)}<span class="truncate">Find user <span data-palette-query class="font-semibold"></span></span><span class="${ITEM_META}">UID or email</span></button></div></el-command-group>`;

  return group("sections", "Go to", sections) + group("recipes", "Recipes", recipeItems) + group("ingredients", "Ingredients", ingredientItems) + findUser;
}

/**
 * actions: { getRecipes(), getIngredients(), goToSection(section), openRecipe(id),
 *            openIngredient(key), findUser(query) }
 */
export function initCommandPalette(actions) {
  const dialog = document.getElementById("commandPalette");
  const palette = dialog?.querySelector("el-command-palette");
  const list = dialog?.querySelector("el-command-list");
  const input = dialog?.querySelector("input");
  if (!dialog || !palette || !list || !input) return;

  const close = () => dialog.closest("el-dialog")?.hide?.({ restoreFocus: false });

  const open = () => {
    list.innerHTML = paletteItemsHtml({ recipes: actions.getRecipes(), ingredients: actions.getIngredients() });
    palette.reset?.();
    dialog.closest("el-dialog")?.show?.();
    input.focus();
  };

  customElements.whenDefined("el-command-palette").then(() => {
    palette.setFilterCallback?.(({ query, node, content }) => {
      const q = query.trim().toLocaleLowerCase();
      if (!q) return false;
      if (node.hasAttribute("data-palette-find-user")) return true;
      return content.toLocaleLowerCase().includes(q);
    });
  });

  input.addEventListener("input", () => {
    const label = dialog.querySelector("[data-palette-query]");
    if (label) label.textContent = `“${input.value.trim()}”`;
  });

  list.addEventListener("click", (e) => {
    const item = e.target.closest("button");
    if (!item) return;
    close();
    if (item.dataset.paletteSection) actions.goToSection(item.dataset.paletteSection);
    else if (item.dataset.paletteRecipe) actions.openRecipe(item.dataset.paletteRecipe);
    else if (item.dataset.paletteIngredient) actions.openIngredient(item.dataset.paletteIngredient);
    else if (item.hasAttribute("data-palette-find-user")) actions.findUser(input.value.trim());
  });

  document.querySelectorAll("[data-palette-open]").forEach((button) => button.addEventListener("click", open));
  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !document.getElementById("app")?.hidden) {
      e.preventDefault();
      if (dialog.open) close();
      else open();
    }
  });
}
